import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Palette, Wrench, Search, Truck, MoreHorizontal, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';

// Icon mapping
const TYPE_ICONS = {
  approved_work: Wrench,
  inspection: Search,
  supplier_visit: Truck,
  other: MoreHorizontal,
};

// Preset colors for quick selection
const PRESET_COLORS = [
  '#22c55e', '#16a34a', '#15803d', // Greens
  '#3b82f6', '#2563eb', '#1d4ed8', // Blues
  '#f59e0b', '#d97706', '#b45309', // Ambers
  '#ef4444', '#dc2626', '#b91c1c', // Reds
  '#8b5cf6', '#7c3aed', '#6d28d9', // Purples
  '#ec4899', '#db2777', '#be185d', // Pinks
  '#64748b', '#475569', '#334155', // Slates
  '#06b6d4', '#0891b2', '#0e7490', // Cyans
];

// Function to generate a lighter background color from accent color
function generateBgColor(color) {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Create a very light version (mix with white at 85%)
  const lightR = Math.round(r + (255 - r) * 0.85);
  const lightG = Math.round(g + (255 - g) * 0.85);
  const lightB = Math.round(b + (255 - b) * 0.85);
  
  return `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`;
}

export default function AppointmentTypeSettingsDialog({ open, onClose }) {
  const { types, updateTypes, refreshTypes } = useAppointmentTypes();
  const [editedTypes, setEditedTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(null);

  useEffect(() => {
    if (open && types) {
      setEditedTypes(JSON.parse(JSON.stringify(types)));
      setSelectedTypeIndex(null);
    }
  }, [open, types]);

  const handleColorChange = (index, color) => {
    const newTypes = [...editedTypes];
    newTypes[index].color = color;
    newTypes[index].bgColor = generateBgColor(color);
    setEditedTypes(newTypes);
  };

  const handleLabelChange = (index, label) => {
    const newTypes = [...editedTypes];
    newTypes[index].label = label;
    setEditedTypes(newTypes);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTypes(editedTypes);
      toast.success('Appointment type colors updated!');
      onClose();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEditedTypes([
      { value: 'approved_work', label: 'Approved Work', color: '#22c55e', bgColor: '#dcfce7' },
      { value: 'inspection', label: 'Inspection', color: '#3b82f6', bgColor: '#dbeafe' },
      { value: 'supplier_visit', label: 'Supplier Visit', color: '#f59e0b', bgColor: '#fef3c7' },
      { value: 'other', label: 'Other', color: '#64748b', bgColor: '#f1f5f9' },
    ]);
    toast.info('Reset to default colors');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh]" data-testid="appointment-type-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-[#0f172a]">
            <Palette className="w-5 h-5 text-[#2563eb]" />
            Customize Appointment Types
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <p className="text-sm text-slate-500">
            Customize the colors and labels for each appointment type. Changes will be reflected across the entire application.
          </p>

          {/* Type List */}
          <div className="space-y-3">
            {editedTypes.map((type, index) => {
              const IconComponent = TYPE_ICONS[type.value];
              const isSelected = selectedTypeIndex === index;

              return (
                <div 
                  key={type.value}
                  className={`p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected ? 'border-[#2563eb] ring-2 ring-[#2563eb]/20' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedTypeIndex(isSelected ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    {/* Preview Icon */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: type.color }}
                    >
                      <IconComponent size={20} className="text-white" />
                    </div>

                    {/* Label Input */}
                    <div className="flex-1">
                      <Input
                        value={type.label}
                        onChange={(e) => handleLabelChange(index, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-sm"
                        placeholder="Type label"
                      />
                    </div>

                    {/* Color Preview */}
                    <div 
                      className="w-8 h-8 rounded-full border-2 border-white shadow-md flex-shrink-0"
                      style={{ backgroundColor: type.color }}
                    />
                  </div>

                  {/* Expanded Color Picker */}
                  {isSelected && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <Label className="text-xs text-slate-500 mb-2 block">Select Color</Label>
                      
                      {/* Preset Colors */}
                      <div className="grid grid-cols-8 gap-2 mb-3">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleColorChange(index, color);
                            }}
                            className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                              type.color === color ? 'border-slate-800 ring-2 ring-offset-1 ring-slate-400' : 'border-white shadow-sm'
                            }`}
                            style={{ backgroundColor: color }}
                          >
                            {type.color === color && (
                              <Check size={14} className="text-white mx-auto" />
                            )}
                          </button>
                        ))}
                      </div>

                      {/* Custom Color Input */}
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500">Custom:</Label>
                        <input
                          type="color"
                          value={type.color}
                          onChange={(e) => handleColorChange(index, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-8 h-8 rounded cursor-pointer border border-slate-200"
                        />
                        <Input
                          value={type.color}
                          onChange={(e) => handleColorChange(index, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-24 font-mono text-xs"
                          placeholder="#000000"
                        />
                      </div>

                      {/* Preview */}
                      <div className="mt-3 p-2 rounded" style={{ backgroundColor: type.bgColor }}>
                        <div className="flex items-center gap-2 text-sm">
                          <div 
                            className="w-5 h-5 rounded flex items-center justify-center"
                            style={{ backgroundColor: type.color }}
                          >
                            <IconComponent size={12} className="text-white" />
                          </div>
                          <span className="font-medium" style={{ color: type.color }}>{type.label}</span>
                          <span className="text-xs text-slate-500">- Preview</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="w-full sm:w-auto"
          >
            Reset to Default
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 sm:flex-none bg-[#2563eb] hover:bg-[#1d4ed8]"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
